/**
 * InternalLinks Component
 * For økt dwell time og intern autoritet
 * @version 1.0.0
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageSquare, FileText, Home, Briefcase, 
  Users, ShoppingBag, ArrowRight, Sparkles 
} from 'lucide-react';

// Module link configurations
const MODULE_LINKS = {
  husleierett: {
    path: '/husleierett',
    label: 'Husleierett',
    description: 'Leiekontrakt, depositum & vedlikehold',
    icon: Home,
    badge: 'Master'
  },
  arbeidsrett: {
    path: '/arbeidsrett',
    label: 'Arbeidsrett',
    description: 'Oppsigelse, sluttavtale & kontrakt',
    icon: Briefcase,
    badge: 'Master'
  },
  forbrukerrett: {
    path: '/forbrukerrett',
    label: 'Forbrukerrett',
    description: 'Reklamasjon & klageveiviser',
    icon: ShoppingBag,
    badge: 'Aktiv'
  },
  barnevern: {
    path: '/barnevern',
    label: 'Barnevern',
    description: 'Juridisk veiledning & dokumenthjelp',
    icon: Users,
    badge: 'Aktiv'
  },
  chat: {
    path: '/chat',
    label: 'Juridisk Chat',
    description: 'Spør om dine rettigheter',
    icon: MessageSquare,
    badge: 'AI'
  },
  documents: {
    path: '/documents',
    label: 'Dokumenter',
    description: 'Dine genererte dokumenter',
    icon: FileText,
    badge: null
  }
};

/**
 * Related Modules Card Component
 * Displays related modules at the bottom of each page
 */
export function RelatedModules({ currentModule, maxItems = 3 }) {
  const relatedModules = Object.entries(MODULE_LINKS)
    .filter(([key]) => key !== currentModule)
    .slice(0, maxItems);
  
  return (
    <div className="mt-12 pt-8 border-t border-[var(--border-subtle)]">
      <h3 className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-4">
        Utforsk andre rettsområder
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {relatedModules.map(([key, module]) => {
          const Icon = module.icon;
          return (
            <Link
              key={key}
              to={module.path}
              className="group flex items-start gap-3 p-4 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:border-[var(--copper)]/40 transition-all duration-200"
              data-testid={`related-module-${key}`}
            >
              <div className="p-2 rounded-lg bg-[var(--bg-highlight)] group-hover:bg-[var(--copper)]/10 transition-colors">
                <Icon className="w-5 h-5 text-[var(--copper)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--text-main)] group-hover:text-[var(--copper)] transition-colors">
                    {module.label}
                  </span>
                  {module.badge && (
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${
                      module.badge === 'Master' 
                        ? 'bg-[var(--copper)]/20 text-[var(--copper)]' 
                        : module.badge === 'AI'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {module.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">
                  {module.description}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--copper)] group-hover:translate-x-1 transition-all" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Quick Actions Bar
 * Shows quick access to chat and document generator
 */
export function QuickActions({ currentModule }) {
  return (
    <div className="flex flex-wrap gap-3 mb-6">
      <Link
        to="/chat"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:border-[var(--copper)]/40 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all"
        data-testid="quick-action-chat"
      >
        <MessageSquare className="w-4 h-4" />
        <span>Spør juridisk assistent</span>
      </Link>
      <Link
        to="/documents"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:border-[var(--copper)]/40 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all"
        data-testid="quick-action-documents"
      >
        <FileText className="w-4 h-4" />
        <span>Mine dokumenter</span>
      </Link>
    </div>
  );
}

/**
 * Module Navigation Breadcrumb
 */
export function ModuleBreadcrumb({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-2 text-sm">
        <li>
          <Link to="/" className="text-[var(--text-muted)] hover:text-[var(--copper)] transition-colors">
            Hjem
          </Link>
        </li>
        {items.map((item, index) => (
          <li key={index} className="flex items-center gap-2">
            <span className="text-[var(--text-muted)]">/</span>
            {item.path ? (
              <Link to={item.path} className="text-[var(--text-muted)] hover:text-[var(--copper)] transition-colors">
                {item.label}
              </Link>
            ) : (
              <span className="text-[var(--text-main)]">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * CTA Banner for cross-module promotion
 */
export function CrossModuleCTA({ targetModule }) {
  const module = MODULE_LINKS[targetModule];
  if (!module) return null;
  
  const Icon = module.icon;
  
  return (
    <Link
      to={module.path}
      className="block p-4 rounded-xl bg-gradient-to-r from-[var(--copper)]/10 to-transparent border border-[var(--copper)]/20 hover:border-[var(--copper)]/40 transition-all group"
      data-testid={`cta-${targetModule}`}
    >
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-[var(--copper)]/20">
          <Icon className="w-6 h-6 text-[var(--copper)]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--text-main)]">{module.label}</span>
            {module.badge === 'Master' && (
              <span className="px-2 py-0.5 text-[10px] font-bold bg-[var(--copper)]/20 text-[var(--copper)] rounded flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Master
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--text-muted)]">{module.description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-[var(--copper)] group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

export default RelatedModules;
